// An emailed share or invitation link goes straight to the log-in page or
// the registration page, carrying its token as a path segment, the way
// every other token this app handles travels: /login/share/:token,
// /login/invitation/:token, /registration/share/:token,
// /registration/invitation/:token. Each page keeps the token on its link
// to the other, so the visitor can switch without losing it, and a visitor
// who is already signed in is handed to the landing, which answers
// whether the thing is theirs.
type EmailedLink = { kind: 'share' | 'invitation'; token: string };

// The link the current route carries, if any: the route's `token` param
// and the segment before it.
const emailedLinkFrom = (
  pathname: string,
  token: string | undefined,
): EmailedLink | undefined => {
  if (!token) {
    return undefined;
  }
  if (/\/share\/[^/]+$/.test(pathname)) {
    return { kind: 'share', token };
  }
  if (/\/invitation\/[^/]+$/.test(pathname)) {
    return { kind: 'invitation', token };
  }
  return undefined;
};

const landingFor = (link: EmailedLink | undefined): string | undefined => {
  if (!link) {
    return undefined;
  }
  const token = encodeURIComponent(link.token);
  return link.kind === 'share'
    ? `/shares/accept/${token}`
    : `/invitations/accept/${token}`;
};

// The log-in page for the link.
const loginFor = (link: EmailedLink): string =>
  `/login/${link.kind}/${encodeURIComponent(link.token)}`;

// The registration page for the link.
const registrationFor = (link: EmailedLink): string =>
  `/registration/${link.kind}/${encodeURIComponent(link.token)}`;

const acceptingNotice = (link: EmailedLink | undefined): string | undefined => {
  if (!link) {
    return undefined;
  }
  return link.kind === 'share'
    ? 'to accept the disclosure shared with you'
    : 'to accept your invitation';
};

export {
  acceptingNotice,
  emailedLinkFrom,
  landingFor,
  loginFor,
  registrationFor,
};
export type { EmailedLink };
